import { useState } from "react";
import { AlertCircle, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { ToolShell, OptionRow, ToolNav } from "@/pages/tools/_shared";
import { cn } from "@/lib/utils";

const INDUSTRIES = [
  "Logistics & transportation",
  "Retail & e-commerce",
  "Government & public sector",
  "Banking & financial services",
  "Healthcare",
  "Real estate & facilities",
  "Education",
  "Manufacturing & distribution",
  "Automotive",
];
const PAIN_POINTS = [
  "Too much manual data entry",
  "Customer support inbox overloaded",
  "Reports take days to produce",
  "Fraud / anomalies going unnoticed",
  "Sales team spends time on unqualified leads",
  "Documents (invoices, POs, forms) are still manual",
  "Field ops needs faster decisions",
  "Compliance evidence collection is painful",
];

const LIBRARY = [
  { id: "copilot", title: "AI copilot for internal teams", effort: "Medium", value: "High", match: ["Too much manual data entry", "Customer support inbox overloaded"] },
  { id: "doc-ai", title: "Document processing (invoices, POs, forms)", effort: "Low", value: "High", match: ["Documents (invoices, POs, forms) are still manual", "Too much manual data entry"] },
  { id: "anomaly", title: "Anomaly detection on transactions", effort: "Medium", value: "High", match: ["Fraud / anomalies going unnoticed"] },
  { id: "lead-scoring", title: "AI lead scoring and routing", effort: "Low", value: "Medium", match: ["Sales team spends time on unqualified leads"] },
  { id: "chat-support", title: "Customer support triage with LLM", effort: "Low", value: "High", match: ["Customer support inbox overloaded"] },
  { id: "reports", title: "Natural-language report generator", effort: "Medium", value: "Medium", match: ["Reports take days to produce"] },
  { id: "field-decisions", title: "On-device decision assistant for field ops", effort: "High", value: "High", match: ["Field ops needs faster decisions"] },
  { id: "evidence", title: "Automated evidence collection for compliance", effort: "Medium", value: "High", match: ["Compliance evidence collection is painful"] },
];

function suggest(industry, pains) {
  const scored = LIBRARY.map((l) => ({
    ...l,
    score: l.match.reduce((s, m) => s + (pains.includes(m) ? 2 : 0), 0),
  }));
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 3).map(({ id, title, effort, value }) => ({ id, title, effort, value }));
}

export default function AIOpportunity() {
  const [step, setStep] = useState(0);
  const [industry, setIndustry] = useState("");
  const [pains, setPains] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", company: "" });
  const [state, setState] = useState({ stage: "quiz", error: "" });
  const total = 3;

  const togglePain = (p) =>
    setPains((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  if (state.stage === "result") {
    const suggestions = state.result;
    return (
      <ToolShell eyebrow="AI Opportunity Finder" title="Three ideas that fit your business">
        <div className="space-y-4">
          {suggestions.map((s, i) => (
            <div key={s.id} data-testid={`ai-suggestion-${s.id}`} className="rounded-lg border border-wafa-deep/10 bg-white p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-wafa-primary text-white">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div className="font-heading text-lg font-semibold text-wafa-deep">{s.title}</div>
                </div>
                <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider">
                  <span className="rounded-full bg-wafa-cream px-2 py-1 text-wafa-deep">Effort · {s.effort}</span>
                  <span className="rounded-full bg-wafa-primary/10 px-2 py-1 text-wafa-deep">Value · {s.value}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <a href="/contact" data-testid="ai-result-cta" className="mt-8 inline-flex w-full items-center justify-center rounded-md bg-wafa-primary px-5 py-3 text-sm font-semibold text-white hover:bg-wafa-deep">
          Discuss these with an engineer
        </a>
      </ToolShell>
    );
  }

  if (state.stage === "gate") {
    const submit = async (e) => {
      e.preventDefault();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
        setState({ ...state, error: "Please enter a valid email address." });
        return;
      }
      const suggestions = suggest(industry, pains);
      try {
        await api.post("/tools/ai-opportunity", {
          industry,
          pain_points: pains,
          suggestions,
          email: contact.email,
          name: contact.name,
          company: contact.company,
        });
        setState({ stage: "result", result: suggestions });
      } catch (err) {
        setState({ ...state, error: "Failed to send. Please try again." });
      }
    };
    return (
      <ToolShell eyebrow="AI Opportunity Finder" title="See your ideas" step={total} totalSteps={total}>
        <form onSubmit={submit} className="space-y-4" data-testid="ai-gate-form">
          <input placeholder="Your name" data-testid="ai-name" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
          <input required type="email" placeholder="Work email *" data-testid="ai-email" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
          <input placeholder="Company" data-testid="ai-company" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
          {state.error ? <div className="flex items-center gap-2 text-sm text-wafa-tomato"><AlertCircle className="h-4 w-4" />{state.error}</div> : null}
          <button data-testid="ai-see-result" className="w-full rounded-md bg-wafa-primary py-3 text-sm font-semibold text-white hover:bg-wafa-deep">See my three ideas</button>
        </form>
      </ToolShell>
    );
  }

  return (
    <ToolShell eyebrow="AI Opportunity Finder" title="Where AI actually helps in your business." subtitle="Two quick picks. We'll return three ideas ranked by effort vs value." step={step + 1} totalSteps={total}>
      {step === 0 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">What industry are you in?</div>
          <div className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {INDUSTRIES.map((i) => (
              <OptionRow key={i} value={i} current={industry} onChange={setIndustry} label={i} testId={`ai-industry-${i.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
            ))}
          </div>
        </div>
      )}
      {step === 1 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">Which of these hurt right now? Pick as many as apply.</div>
          <div className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {PAIN_POINTS.map((p) => {
              const active = pains.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => togglePain(p)}
                  data-testid={`ai-pain-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={cn(
                    "rounded-md border px-4 py-3 text-start text-sm transition-colors",
                    active
                      ? "border-wafa-primary bg-wafa-primary/5 text-wafa-deep font-semibold"
                      : "border-wafa-deep/15 bg-white text-wafa-ink hover:border-wafa-deep/40",
                  )}
                >
                  {p}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <ToolNav
        onBack={step > 0 ? () => setStep(step - 1) : null}
        onNext={() => { if (step === 1) setState({ stage: "gate", error: "" }); else setStep(step + 1); }}
        disableNext={step === 0 ? !industry : pains.length === 0}
        nextLabel={step === 1 ? "Finish" : "Next"}
      />
    </ToolShell>
  );
}
